import { LogLevel } from '@grafana/faro-core';
import type { SSOLogArgs } from './types';

const BUSINESS_ROUTE_EXCLUSIONS = ['/iam', '/user', '/faro'];
const LOG_MASK_PLACEHOLDER = '***';
const FALLBACK_ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';

const ensureUrl = (input: string): URL | undefined => {
    try {
        return input.startsWith('http') ? new URL(input) : new URL(input, FALLBACK_ORIGIN);
    } catch {
        return undefined;
    }
};

export const normalizeLogContextValue = (value: unknown): string | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (value === null) {
        return 'null';
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return '[unserializable-object]';
        }
    }

    return String(value);
};

export const buildLogContext = (logArgs: SSOLogArgs): Record<string, string> => {
    const { metadata, ...rest } = logArgs;
    const basePayload: Record<string, unknown> = {
        ...rest,
        timestamp: new Date().toISOString(),
        ...metadata
    };

    return Object.entries(basePayload).reduce<Record<string, string>>((context, [key, value]) => {
        const normalizedValue = normalizeLogContextValue(value);

        if (normalizedValue !== undefined) {
            context[key] = normalizedValue;
        }

        return context;
    }, {});
};

export const resolveLogLevel = (success?: boolean, error?: string): LogLevel => {
    if (error || success === false) {
        return LogLevel.ERROR;
    }

    return LogLevel.INFO;
};

export const createBusinessRoutesPattern = (routes: string[]): RegExp | undefined => {
    const businessRoutes = routes.filter((route) => !BUSINESS_ROUTE_EXCLUSIONS.includes(route));

    if (!businessRoutes.length) {
        return undefined;
    }

    return new RegExp(`\\/(${businessRoutes.map((route) => route.slice(1)).join('|')})\\/v\\d+\\/`);
};

export const buildIgnorePatterns = (businessRoutesPattern?: RegExp): Array<string | RegExp> => {
    const patterns: Array<string | RegExp> = [/\/faro/, /\/graphql/, /\/api\/(?!.*auth)/];

    if (businessRoutesPattern) {
        patterns.push(businessRoutesPattern);
    }

    return patterns;
};

export const buildAuthCorsPatterns = (): Array<string | RegExp> => [
    /\/iam\/v\d+\//,
    /\/user\/v\d+\//,
    /https?:\/\/.+\.amazoncognito\.com\//,
    /https?:\/\/.+\.okta\.com\//,
    /https?:\/\/accounts\.google\.com\//
];

export const sanitizeUrlForLogging = (
    url: string,
    options: { removeParams?: string[]; stripQuery?: boolean } = {}
): string => {
    const { removeParams = [], stripQuery = false } = options;
    const parsedUrl = ensureUrl(url);

    if (!parsedUrl) {
        return url.split('?')[0];
    }

    if (stripQuery) {
        return `${parsedUrl.origin}${parsedUrl.pathname}`;
    }

    removeParams.forEach((param) => {
        parsedUrl.searchParams.delete(param);
    });

    return `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search ? `?${parsedUrl.searchParams.toString()}` : ''}`;
};

const maskValue = (value = ''): string => {
    if (!value) {
        return LOG_MASK_PLACEHOLDER;
    }

    if (value.length <= 2) {
        return `${value.charAt(0)}${LOG_MASK_PLACEHOLDER}`;
    }

    return `${value.charAt(0)}${LOG_MASK_PLACEHOLDER}${value.charAt(value.length - 1)}`;
};

export const maskEmailForLogging = (email?: string): string | undefined => {
    if (!email) {
        return undefined;
    }

    const trimmed = email.trim().toLowerCase();
    const [localPart, domain] = trimmed.split('@');

    if (!domain) {
        return LOG_MASK_PLACEHOLDER;
    }

    return `${maskValue(localPart)}@${domain}`;
};

export const maskIdentifierForLogging = (identifier?: string): string | undefined => {
    if (!identifier) {
        return undefined;
    }

    return maskValue(identifier.trim());
};
